<template>
  <b-row>
    <form @submit.prevent class="col-12 col-sm-9">
      <b-card class="mb-3" :header="heading" no-body>
        <div class="card-body">
          <b-row class="no-gutters form-project">
            <b-col cols="12">
              <b-row>
                <b-form-group class="col-6" label="Name:">
                  <b-form-input
                    id="form-input-name"
                    v-model="itemForm.name"
                    v-validate="{ required: true, min: 2 }"
                    name="form-input-name"
                    :state="validateState('form-input-name')"
                    data-vv-as="Name"
                  />
                  <b-form-invalid-feedback id="form-input-name-feedback">
                    {{ veeErrors.first('form-input-name') }}
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group class="col-6" label="Brand:" v-if="itemForm.ownership == 'ClientOwned'">
                  <b-v-select
                    id="form-input-brandId"
                    v-model="itemForm.brandId"
                    v-validate="{ required: true }"
                    :searchable="true"
                    name="form-input-brandId"
                    :state="validateState('form-input-brandId')"
                    data-vv-as="Brand"
                    :options="brandsList"
                  />
                  <b-form-invalid-feedback id="form-input-brandId-feedback">
                    {{ veeErrors.first('form-input-brandId') }}
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group class="col-6" label="Type:">
                  <b-v-select
                    id="form-input-type"
                    v-model="itemForm.type"
                    v-validate="{ required: true }"
                    :searchable="true"
                    name="form-input-type"
                    :state="validateState('form-input-type')"
                    data-vv-as="Type"
                    :options="typeOptions"
                  />
                  <b-form-invalid-feedback id="form-input-type-feedback">
                    {{ veeErrors.first('form-input-type') }}
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group class="col-6" label="Ownership:">
                  <b-v-select
                    id="form-input-ownership"
                    v-model="itemForm.ownership"
                    v-validate="{ required: true }"
                    :searchable="true"
                    name="form-input-ownership"
                    :state="validateState('form-input-ownership')"
                    data-vv-as="Ownership"
                    :options="ownershipOptions"
                  />
                  <b-form-invalid-feedback id="form-input-ownership-feedback">
                    {{ veeErrors.first('form-input-ownership') }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-row>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <!-- Import Sheet Mappings Section -->
      <b-card class="mb-3" header="Column Mappings" no-body>
        <div class="card-body">
          <div v-if="itemForm.importSheetMappings && itemForm.importSheetMappings.length === 0" class="text-muted text-center py-4">
            No mappings defined. Use the form below to add mappings.
          </div>

          <div v-for="(mapping, index) in sortedMappings" :key="mapping.id || mapping._uid || index" class="border rounded px-2 py-2 mb-1">
            <b-row>
            <b-form-group class="col-2" label="From Sheet (optional):">
              <b-form-input
                v-model="mapping.fromSheetName"
                type="text"
                :name="'mapping-fromSheetName-' + index"
              />
            </b-form-group>
              <b-form-group class="col-3" label="Column ID:">
                <b-input-group :append="columnIdToLetter(mapping.fromColumnId)">
                  <b-form-input
                    v-model.number="mapping.fromColumnId"
                    type="number"
                    min="1"
                    max="100"
                    :name="'mapping-fromColumnId-' + index"
                    placeholder="e.g. 1 for column A"
                  />
                </b-input-group>
              </b-form-group>

              <b-form-group class="col-3" label="Group:">
                <b-v-select
                  v-model="mapping.mapGroup"
                  :searchable="true"
                  :options="mapGroupOptions"
                  :name="'mapping-mapGroup-' + index"
                  :disabled="true"
                />
              </b-form-group>

              <b-form-group class="col-3" label="Map To Property:" v-if="mapping.mapGroup == 'Orders'">
                <b-v-select
                  v-model="mapping.toProp"
                  :searchable="true"
                  :options="orderPropertyOptions"
                  :name="'mapping-toProp-' + index"
                />
              </b-form-group>
              <b-form-group class="col-3" label="Map To Property:" v-else>
                <b-v-select
                  v-model="mapping.toProp"
                  :searchable="true"
                  :options="lineItemPropertyOptions"
                  :name="'mapping-toProp-' + index"
                />
              </b-form-group>

              <b-form-group class="col-1 d-flex align-items-end">
                <b-button variant="danger" size="sm" @click="removeMapping(mapping)">
                  <i class="fa fa-trash"></i>
                  Remove
                </b-button>
              </b-form-group>
            </b-row>
          </div>
        </div>

        <!-- Add Mapping Form in Footer -->
        <div class="card-footer">
          <h6 class="mb-3">Add New Mapping</h6>
          <b-row>
            <b-form-group class="col-2" label="From Sheet (optional):" description="Name of Sheet (exact!), if using excel files, and it is in separate sheet.">
              <b-form-input
                v-model="newMapping.fromSheetName"
                type="text"
              />
            </b-form-group>
            <b-form-group class="col-3" label="Excel Column:">
              <b-input-group :append="columnIdToLetter(newMapping.fromColumnId)">
                <b-form-input
                  v-model.number="newMapping.fromColumnId"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="e.g. 1 for column A"
                />
              </b-input-group>
              <small class="text-muted">Column number (A=1, B=2, etc.)</small>
            </b-form-group>

            <b-form-group class="col-3" label="Group:">
              <b-v-select
                v-model="newMapping.mapGroup"
                :searchable="true"
                :options="mapGroupOptions"
                @input="onNewMappingGroupChanged"
              />
            </b-form-group>

            <b-form-group class="col-3" label="Map To Property:" v-if="newMapping.mapGroup == 'Orders'">
              <b-v-select
                v-model="newMapping.toProp"
                :searchable="true"
                :options="orderPropertyOptions"
              />
            </b-form-group>
            <b-form-group class="col-3" label="Map To Property:" v-else>
              <b-v-select
                v-model="newMapping.toProp"
                :searchable="true"
                :options="lineItemPropertyOptions"
              />
            </b-form-group>

            <b-form-group class="col-1 d-flex align-items-end">
              <b-button
                variant="success"
                @click="addMapping"
                size="sm"
                :disabled="!canAddMapping"
                class="mb-3"
              >Add New</b-button>
            </b-form-group>
          </b-row>
        </div>
      </b-card>
    </form>

    <aside class="col-12 col-sm-3">
      <b-card header="Actions">
        <b-button
          :disabled="savingItem"
          variant="primary"
          block
          type="submit"
          @click="SaveForm"
        >
          <span v-if="savingItem">Saving...</span>
          <span v-else>Save Changes</span>
        </b-button>
        <router-link
          :disabled="savingItem"
          class="mt-2 btn btn-block btn-outline-danger"
          :to="{ name: 'Transfers.ImportSheetMappingMethods.Index' }"
        >
          <span>Cancel</span>
        </router-link>
      </b-card>

      <b-card header="Example File" class="mt-3">
        <div v-if="itemForm.exampleFileUrl" class="mb-3">
          <small class="text-muted d-block mb-1">Current file:</small>
          <div class="d-flex">
            <a :href="itemForm.exampleFileUrl" target="_blank" class="btn btn-sm btn-outline-primary flex-grow-1">
              <i class="fa fa-download mr-1"></i> Download Example
            </a>
            <b-button variant="outline-danger" size="sm" class="ml-1" @click="removeExampleFile" title="Remove file">
              <i class="fa fa-times"></i>
            </b-button>
          </div>
        </div>

        <b-form-group :label="itemForm.exampleFileUrl ? 'Replace file:' : 'Upload file:'" class="mb-2">
          <b-form-file
            v-model="exampleFile"
            accept=".xlsx,.xls,.csv"
            placeholder="Choose a file..."
            drop-placeholder="Drop file here..."
            size="sm"
          ></b-form-file>
        </b-form-group>

        <b-button
          variant="outline-success"
          size="sm"
          block
          :disabled="!exampleFile || uploadingExampleFile"
          @click="uploadExampleFile"
        >
          <b-spinner small v-if="uploadingExampleFile" class="mr-1"></b-spinner>
          {{ uploadingExampleFile ? 'Uploading...' : 'Upload' }}
        </b-button>
      </b-card>
    </aside>
  </b-row>
</template>

<script>
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  mixins: [crudMixinGen('importSheetMappingMethods'), myCommonDataMixin],
  props: {
    id: {
      type: String,
      default: '',
      required: false
    }
  },

  data: () => ({
    heading: 'Import Sheet Mapping Method',
    exampleFile: null,
    uploadingExampleFile: false,
    newMapping: {
      fromColumnId: 1,
      toProp: 'OrderNumber',
      mapGroup: 'Orders'
    },
    typeOptions: [
      { value: 'ManualShipmentImport', text: 'Manual Shipment Import' },
      { value: 'StorefrontOrderImport', text: 'Storefront Order Import' }
    ],
    ownershipOptions: [
      { value: 'Everyone', text: 'Everyone' },
      { value: 'ClientOwned', text: 'Client Owned' }
    ],
    mapGroupOptions: [
      { value: 'Orders', text: 'Order Details' },
      { value: 'LineItems', text: 'Line Items' }
    ],
    orderPropertyOptions: [
      // Order/Shipment level properties
      { value: 'OrderNumber', text: 'Order Number' },
      { value: 'CustomReferenceNumber', text: 'Custom Reference Number' },
      { value: 'ExpectedDate', text: 'Expected Date' },
      { value: 'PoNumber', text: 'PO Number' },
      { value: 'ContactEmail', text: 'Contact Email' },
      { value: 'ShipToName', text: 'Ship To Name' },
      { value: 'ShipToAddressLine1', text: 'Ship To Address Line 1' },
      { value: 'ShipToAddressLine2', text: 'Ship To Address Line 2' },
      { value: 'ShipToAddressLine3', text: 'Ship To Address Line 3' },
      { value: 'ShipToCity', text: 'Ship To City' },
      { value: 'ShipToState', text: 'Ship To State' },
      { value: 'ShipToPostcode', text: 'Ship To Postcode' },
      { value: 'ShipToCountryCode', text: 'Ship To Country Code' },
      { value: 'ShipToPhone', text: 'Ship To Phone' },
      { value: 'BillToName', text: 'Bill To Name' },
      { value: 'BillToAddressLine1', text: 'Bill To Address Line 1' },
      { value: 'BillToAddressLine2', text: 'Bill To Address Line 2' },
      { value: 'BillToAddressLine3', text: 'Bill To Address Line 3' },
      { value: 'BillToCity', text: 'Bill To City' },
      { value: 'BillToState', text: 'Bill To State' },
      { value: 'BillToPostcode', text: 'Bill To Postcode' },
      { value: 'BillToCountryCode', text: 'Bill To Country Code' },
      { value: 'BillToPhone', text: 'Bill To Phone' },
      { value: 'RequestedShipCode', text: 'Requested Ship Code' },
      { value: 'RequestedCarrier', text: 'Requested Carrier' },
      { value: 'RequestedService', text: 'Requested Service' },
      { value: 'CustomField1', text: 'Custom Field 1' },
      { value: 'CustomField2', text: 'Custom Field 2' },
      { value: 'CustomField3', text: 'Custom Field 3' },
      { value: 'CustomField4', text: 'Custom Field 4' },
      { value: 'CustomField5', text: 'Custom Field 5' },
      { value: 'OrderOtherCustomData', text: 'Forward to Custom Fields' }
    ],
    lineItemPropertyOptions: [
      // Line Item level properties
      { value: 'LineItemSku', text: 'Line Item SKU' },
      { value: 'LineItemDescription', text: 'Line Item Description' },
      { value: 'LineItemQuantity', text: 'Line Item Quantity' },
      { value: 'LineItemIsAdjustment', text: 'Line Item Is Adjustment' },
      { value: 'LineItemReqLotNumber', text: 'Line Item Required Lot Number' },
      { value: 'ItemCustomField1', text: 'Item Custom Field 1' },
      { value: 'ItemCustomField2', text: 'Item Custom Field 2' },
      { value: 'ItemCustomField3', text: 'Item Custom Field 3' },
      { value: 'ItemOtherCustomData', text: 'Forward to Custom Fields' }
    ]
  }),

  computed: {
    canAddMapping() {
      return this.newMapping.fromColumnId > 0 &&
             this.newMapping.toProp &&
             this.newMapping.mapGroup;
    },
    sortedMappings() {
      const mappings = this.itemForm.importSheetMappings || [];
      // Sort a shallow copy so the underlying array order is preserved; elements are the
      // same object references, so v-model edits still mutate the real mappings.
      return [...mappings].sort((a, b) => {
        const sheetA = (a.fromSheetName || '').toLowerCase();
        const sheetB = (b.fromSheetName || '').toLowerCase();
        if (sheetA !== sheetB) {
          return sheetA < sheetB ? -1 : 1;
        }
        return (a.fromColumnId || 0) - (b.fromColumnId || 0);
      });
    }
  },

  mounted() {
    if (this.id) {
      this.ResetForm();
      this.LoadItem(this.id);
    } else {
      this.ResetForm();
    }
  },

  methods: {
    columnIdToLetter(columnId) {
      const num = parseInt(columnId, 10);
      if (!num || num < 1) {
        return '';
      }
      let n = num;
      let letters = '';
      while (n > 0) {
        const remainder = (n - 1) % 26;
        letters = String.fromCharCode(65 + remainder) + letters;
        n = Math.floor((n - 1) / 26);
      }
      return letters;
    },
    getPropertyOptionsForGroup(group) {
      if (group === 'LineItems') {
        return this.lineItemPropertyOptions;
      }
      return this.orderPropertyOptions;
    },
/*
    onGroupChanged(mapping, index) {
      // Reset the ToProp when group changes to avoid invalid combinations
      const options = this.getPropertyOptionsForGroup(mapping.mapGroup);
      if (options.length > 0) {
        mapping.toProp = options[0].value;
      }
    },
*/
    onNewMappingGroupChanged() {
      // Reset the ToProp when group changes in new mapping form
      const options = this.getPropertyOptionsForGroup(this.newMapping.mapGroup);
      if (options.length > 0) {
        this.newMapping.toProp = options[0].value;
      }
    },

    addMapping() {
      if (!this.canAddMapping) {
        return;
      }

      if (!this.itemForm.importSheetMappings) {
        this.itemForm.importSheetMappings = [];
      }

      // Check if column is already mapped within the same sheet (columns can be reused across different sheets)
      const normalizeSheet = s => (s || '').trim().toLowerCase();
      const newSheet = normalizeSheet(this.newMapping.fromSheetName);
      const existingMapping = this.itemForm.importSheetMappings.find(
        m => m.fromColumnId === this.newMapping.fromColumnId && normalizeSheet(m.fromSheetName) === newSheet
      );
      if (existingMapping) {
        const sheetLabel = this.newMapping.fromSheetName ? ` in sheet "${this.newMapping.fromSheetName}"` : '';
        this.$swal({ title: `Column ${this.newMapping.fromColumnId} is already mapped${sheetLabel}`, icon: 'warning' });
        return;
      }

      this.itemForm.importSheetMappings.push({
        id: null,
        _uid: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        fromSheetName: this.newMapping.fromSheetName,
        fromColumnId: this.newMapping.fromColumnId,
        toProp: this.newMapping.toProp,
        mapGroup: this.newMapping.mapGroup,
        importSheetMappingMethodId: this.itemForm.id || ''
      });

      // Reset the form
      this.newMapping = {
        fromSheetName: '',
        fromColumnId: this.newMapping.fromColumnId + 1,
        toProp: this.getPropertyOptionsForGroup(this.newMapping.mapGroup)[0].value,
        mapGroup: this.newMapping.mapGroup
      };
    },

    removeMapping(mapping) {
      const idx = this.itemForm.importSheetMappings.indexOf(mapping);
      if (idx !== -1) {
        this.itemForm.importSheetMappings.splice(idx, 1);
      }
    },

    removeExampleFile() {
      this.itemForm.exampleFileUrl = null;
    },

    uploadExampleFile() {
      var self = this;
      if (!self.exampleFile) return;

      self.uploadingExampleFile = true;

      var formData = new FormData();
      formData.append('formFile', self.exampleFile);

      var url = '/api/common/import-sheet-mapping-methods/upload-example-file';
      if (self.itemForm.id) {
        url += '?importSheetMappingMethodId=' + self.itemForm.id;
      }

      self.$http.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
        .then(function(response) {
          if (response.data && response.data.url) {
            self.itemForm.exampleFileUrl = response.data.url;
            self.$swal({ title: 'Example file uploaded successfully!', icon: 'success' });
          }
          self.exampleFile = null;
        })
        .catch(function(error) {
          console.error('Upload error:', error);
          var errorMsg = (error.response && error.response.data && error.response.data.errorMessage);
          if (!errorMsg) {
            errorMsg = 'Failed to upload example file';
          }
          self.$swal({ title: errorMsg, icon: 'error' });
        })
        .finally(function() {
          self.uploadingExampleFile = false;
        });
    },

    SaveForm() {
      this.$validator.validateAll()
        .then(result => {
          if (!result) {
            this.$swal({ title: 'Please fix validation errors', icon: 'error' });
            return;
          }

          // Validate that we have at least one mapping
          if (!this.itemForm.importSheetMappings || this.itemForm.importSheetMappings.length === 0) {
            this.$swal({ title: 'Please add at least one column mapping', icon: 'error' });
            return;
          }

          // Validate each mapping has required fields
          for (let mapping of this.itemForm.importSheetMappings) {
            if (!mapping.fromColumnId || !mapping.toProp || !mapping.mapGroup) {
              this.$swal({ title: 'Please fill in all mapping fields', icon: 'error' });
              return;
            }
          }

          this.savingItem = true;
          this.SaveItem()
            .then(resp => {
              this.ResetForm();
              this.$swal({ title: 'Import Sheet Mapping Method saved!', icon: 'success' });
              this.$router.push({
                name: 'Transfers.ImportSheetMappingMethods.Index'
              });
            })
            .catch(err => {
              this.savingItem = false;
              if (err.response && err.response.data && err.response.data.errorMessage) {
                this.$swal({ title: err.response.data.errorMessage, icon: 'error' });
              } else {
                this.$swal({ title: 'Item Was NOT Saved!', icon: 'error' });
              }
            });
        });
    }
  }
};
</script>